import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, switchMap, take } from 'rxjs';
import { AppRoutes, DrillsRoutes } from '@core';
import { DrillType } from '@drills/infra';
import {
  SzAppShellComponent,
  SzButtonComponent,
  SzConfirmDialogService,
  SzCtaBarComponent,
  SzIconComponent,
  SzPageTitleComponent,
  SzSearchInputComponent,
  SzSectionHeadComponent,
  SzSkeletonComponent,
  SzTopBarComponent
} from '@ui';
import {
  DeleteDrillCommandHandler,
  DrillsListQueryHandler,
  DrillsListQueryResult,
  ToggleFavoriteCommandHandler
} from '../../application/handlers';
import { DrillViewModel } from '../../application/models';
import { DrillsListStateService } from '../../application/state/drills-list-state.service';
import { DrillListItemComponent } from '../drill-list-item/drill-list-item.component';
import { DrillsListTab, DrillsListTabsComponent } from '../drills-list-tabs/drills-list-tabs.component';
import {
  DrillsListTypeFilter,
  DrillsListTypeFilterComponent
} from '../drills-list-type-filter/drills-list-type-filter.component';

@Component({
  selector: 'lib-drills-list',
  templateUrl: './drills-list.component.html',
  styleUrls: ['./drills-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzSearchInputComponent,
    SzSectionHeadComponent,
    SzSkeletonComponent,
    SzCtaBarComponent,
    SzButtonComponent,
    SzIconComponent,
    DrillListItemComponent,
    DrillsListTabsComponent,
    DrillsListTypeFilterComponent
  ],
  providers: [DrillsListQueryHandler, ToggleFavoriteCommandHandler, DeleteDrillCommandHandler]
})
export class DrillsListComponent implements OnInit {
  private readonly listHandler: DrillsListQueryHandler = inject(DrillsListQueryHandler);
  private readonly toggleFavoriteHandler: ToggleFavoriteCommandHandler = inject(ToggleFavoriteCommandHandler);
  private readonly deleteHandler: DeleteDrillCommandHandler = inject(DeleteDrillCommandHandler);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly state: DrillsListStateService = inject(DrillsListStateService);

  readonly list: WritableSignal<DrillsListQueryResult | null> = signal<DrillsListQueryResult | null>(null);
  readonly searchQuery: WritableSignal<string> = this.state.searchQuery;
  readonly activeTab: WritableSignal<DrillsListTab> = this.state.activeTab;
  readonly typeFilter: WritableSignal<DrillsListTypeFilter> = this.state.typeFilter;

  readonly totalCount: Signal<number> = computed((): number => this.list()?.all.length ?? 0);
  readonly favoritesCount: Signal<number> = computed((): number => this.list()?.favorites.length ?? 0);

  readonly visibleDrills: Signal<readonly DrillViewModel[]> = computed(() => {
    const data: DrillsListQueryResult | null = this.list();
    if (data === null) return [];
    const tab: DrillsListTab = this.activeTab();
    const tabFiltered: readonly DrillViewModel[] =
      tab === 'favorites' ? data.favorites : tab === 'recent' ? data.recent : data.all;
    const typeFiltered: readonly DrillViewModel[] = this.applyTypeFilter(tabFiltered, this.typeFilter());
    const q: string = this.searchQuery().trim().toLowerCase();
    return q.length === 0 ? typeFiltered : typeFiltered.filter((d: DrillViewModel) => d.name.toLowerCase().includes(q));
  });

  ngOnInit(): void {
    this.state.syncOnEnter();
    this.refresh();
  }

  toggleFavorite(drillId: string): void {
    this.toggleFavoriteHandler
      .execute(drillId)
      .pipe(take(1))
      .subscribe(() => this.refresh());
  }

  deleteDrill(drillId: string): void {
    this.confirmDialog
      .confirm$({
        header: this.translate.instant('drills.list.deleteConfirm'),
        okLabel: this.translate.instant('common.delete'),
        destructive: true
      })
      .pipe(
        filter((ok: boolean): boolean => ok),
        switchMap(() => this.deleteHandler.execute(drillId)),
        take(1)
      )
      .subscribe((): void => this.refresh());
  }

  setSearch(value: string): void {
    this.searchQuery.set(value);
  }

  setTab(tab: DrillsListTab): void {
    this.activeTab.set(tab);
  }

  setTypeFilter(filter: DrillsListTypeFilter): void {
    this.typeFilter.set(filter);
  }

  goToDrillDetail(drillId: string): void {
    void this.router.navigate(['/', AppRoutes.DRILLS, DrillsRoutes.DETAIL, drillId]);
  }

  goToCreateDrill(): void {
    void this.router.navigate(['/', AppRoutes.DRILLS, DrillsRoutes.CREATE]);
  }

  private applyTypeFilter(drills: readonly DrillViewModel[], filter: DrillsListTypeFilter): readonly DrillViewModel[] {
    if (filter === 'all') return drills;
    const target: DrillType = filter === 'dry' ? DrillType.Dry : DrillType.Live;
    return drills.filter((d: DrillViewModel) => d.type === target);
  }

  private refresh(): void {
    this.listHandler
      .query()
      .pipe(take(1))
      .subscribe((r: DrillsListQueryResult) => this.list.set(r));
  }
}
